import { Box, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { EMPTY_PLACEHOLDER } from '../wingmanIntelligenceMetrics';

const ICON_BY_TONE = {
  error: WarningAmberRoundedIcon,
  warning: ErrorOutlineIcon,
  success: CheckCircleOutlineIcon,
};

/** Left rail + text + icon colors aligned to reference UI */
const ALERT_THEME = {
  error: {
    rail: '#E53935',
    bg: '#FEF6F6',
    text: '#C62828',
  },
  warning: {
    rail: '#B7833C',
    bg: '#FFFAF5',
    text: '#6D4C41',
  },
  success: {
    rail: '#43A047',
    bg: '#F3FBF4',
    text: '#2E7D32',
  },
};

const SystemAlertsRow = ({ alerts = [] }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: '#1B1B1F',
          letterSpacing: '-0.01em',
          mb: 1.5,
        }}
      >
        System Alerts
      </Typography>

      {alerts.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#5F6368' }}>{EMPTY_PLACEHOLDER}</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: '16px',
          }}
        >
          {alerts.map((a) => {
            const Icon = a.icon ?? ICON_BY_TONE[a.tone] ?? ErrorOutlineIcon;
            const theme = ALERT_THEME[a.tone] ?? ALERT_THEME.warning;
            return (
              <Box
                key={a.key}
                sx={{
                  display: 'flex',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  bgcolor: theme.bg,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 5,
                    flexShrink: 0,
                    bgcolor: theme.rail,
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: '14px',
                    pr: 2,
                    pl: 1.75,
                    minWidth: 0,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 28,
                      color: theme.text,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15,
                        lineHeight: 1.35,
                        color: theme.text,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {a.title ?? EMPTY_PLACEHOLDER}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: 1.45,
                        color: theme.text,
                        opacity: 0.92,
                        mt: '2px',
                      }}
                    >
                      {a.body ?? EMPTY_PLACEHOLDER}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: theme.text,
                        opacity: 0.62,
                        mt: '10px',
                        display: 'block',
                      }}
                    >
                      {a.time ?? EMPTY_PLACEHOLDER}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SystemAlertsRow;
